/**
 * CocoaPop Animation - "Chocolate Bomb Explosion" (ENHANCED VERSION)
 * 
 * FRAMER MOTION VERSION: Ultimate chocolate bomb experience!
 * - Googly eyes with expression transitions (curious → scared → OH NO!)
 * - Melting chocolate drips during rumbling (tier 3+)
 * - Pre-explosion bounce physics (builds tension)
 * - "UH OH..." warning text during cracking (tier 3+)
 * - Enhanced lava cracks with branching patterns
 * - Smoke rings post-explosion (tier 5+)
 * - Screen edge chocolate splatter (tier 4+)
 * - Chocolate chunks with spring physics
 * - Confetti rain and golden sparkles
 * 
 * Synchronized with sound effects.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useAnimationSound from '../../../hooks/useAnimationSound';
import { getPhasesForCandy } from './soundRegistry';

const CocoaPopAnimation = ({ intensity = 1, onComplete }) => {
    const [phase, setPhase] = useState('materializing');

    // Sound integration
    const { playPhaseSound, preloadSounds } = useAnimationSound('CocoaPop', intensity, true);

    // Device-aware particle scaling for performance
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const particleMultiplier = isMobile ? 0.4 : 1;

    // Scaled effect counts with mobile optimization
    const particleCount = Math.floor((12 + (intensity - 1) * 10) * particleMultiplier);
    const crackCount = Math.floor((5 + intensity * 2) * particleMultiplier);
    const confettiCount = Math.floor((10 + intensity * 5) * particleMultiplier);
    const chocolateChunks = Math.floor((5 + intensity * 3) * particleMultiplier);
    const smokeRingCount = Math.floor(2 + intensity);

    // Tier-based features
    const showFace = true;
    const showScreenShake = intensity >= 4;
    const showEpicShake = intensity >= 6;
    const showGoldenSparkles = intensity >= 3;
    const showSteam = intensity >= 2;
    const showComicText = intensity >= 2;
    const showChocolateRain = intensity >= 5;
    const showMeltingDrips = intensity >= 3;
    const showUhOhText = intensity >= 3;
    const showSmokeRings = intensity >= 5;
    const showSplatter = intensity >= 4;

    // Colors
    const confettiColors = useMemo(() =>
        ['#FF6B9D', '#FFE066', '#FF8A50', '#A78BFA', '#34D399', '#F472B6'], []);
    const chocolateColors = useMemo(() =>
        ['#8B4513', '#D2691E', '#CD853F', '#F4A460', '#FFE4C4', '#5D3A1A'], []);

    // Stable ref for playPhaseSound to avoid re-triggering animation useEffect
    const playPhaseSoundRef = React.useRef(playPhaseSound);
    playPhaseSoundRef.current = playPhaseSound;

    // Stable ref for onComplete
    const onCompleteRef = React.useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Preload sounds (only once on mount)
    useEffect(() => {
        preloadSounds(getPhasesForCandy('CocoaPop'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Extended animation sequence (now up to 2.8s for high tiers)
    useEffect(() => {
        const timers = [];
        const baseTiming = intensity >= 5 ? 1.15 : 1; // Extend timing for high tiers

        playPhaseSoundRef.current('materializing');

        timers.push(setTimeout(() => {
            setPhase('rumbling');
            playPhaseSoundRef.current('rumbling');
        }, 400 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('cracking');
            playPhaseSoundRef.current('cracking');
        }, 750 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('popping');
            playPhaseSoundRef.current('popping');
        }, 1100 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('complete');
            onCompleteRef.current?.();
        }, 2500 * baseTiming));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Generate random values for particles
    const generateParticles = (count) => {
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            angle: Math.random() * 360,
            distance: 90 + Math.random() * 140,
            size: 6 + Math.random() * 14,
            color: chocolateColors[Math.floor(Math.random() * chocolateColors.length)],
            delay: Math.random() * 0.2,
        }));
    };

    const generateChunks = (count) => {
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            angle: Math.random() * 360,
            distance: 70 + Math.random() * 110,
            size: 18 + Math.random() * 25,
            rotation: Math.random() * 720 - 360,
            delay: Math.random() * 0.12,
        }));
    };

    const generateConfetti = (count) => {
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            x: -120 + Math.random() * 240,
            fall: 120 + Math.random() * 120,
            drift: -60 + Math.random() * 120,
            spin: 360 + Math.random() * 1080,
            color: confettiColors[i % confettiColors.length],
            delay: Math.random() * 0.35,
            size: 6 + Math.random() * 10,
        }));
    };

    const generateCracks = (count) => {
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            angle: (360 / count) * i + Math.random() * 20,
            length: 30 + Math.random() * 50,
            delay: i * 0.03,
            hasBranch: Math.random() > 0.5,
        }));
    };

    const generateMeltingDrips = () => {
        return Array.from({ length: 4 }, (_, i) => ({
            id: i,
            startX: -20 + i * 15 + Math.random() * 10,
            delay: 0.2 + i * 0.15,
            length: 15 + Math.random() * 20,
        }));
    };

    const particles = useMemo(() => generateParticles(particleCount), [particleCount]);
    const chunks = useMemo(() => generateChunks(chocolateChunks), [chocolateChunks]);
    const confetti = useMemo(() => generateConfetti(confettiCount), [confettiCount]);
    const cracks = useMemo(() => generateCracks(crackCount), [crackCount]);
    const meltingDrips = useMemo(() => generateMeltingDrips(), []);

    // Animation variants
    const orbVariants = {
        materializing: {
            scale: [0, 1.15, 1],
            opacity: [0, 1],
            y: [20, 0],
            transition: { duration: 0.4, ease: 'easeOut' }
        },
        rumbling: {
            x: [0, -4, 4, -4, 4, 0],
            scale: [1, 1.04, 0.96, 1.04, 1],
            transition: { duration: 0.25, repeat: Infinity, repeatType: 'loop' }
        },
        cracking: {
            scale: [1, 1.15, 1.12],
            y: [0, -5, 0],
            transition: { duration: 0.35, ease: 'easeOut' }
        },
        popping: {
            scale: [1.12, 0],
            opacity: [1, 0],
            transition: { duration: 0.18, ease: 'easeIn' }
        }
    };

    // Face expression based on phase
    const getFaceExpression = () => {
        switch (phase) {
            case 'materializing':
                return 'curious';
            case 'rumbling':
                return 'worried';
            case 'cracking':
                return 'scared';
            default:
                return 'curious';
        }
    };

    const createExplosionAnimation = (angle, distance, delay) => ({
        initial: { x: 0, y: 0, scale: 0, opacity: 1 },
        animate: {
            x: Math.cos((angle * Math.PI) / 180) * distance,
            y: Math.sin((angle * Math.PI) / 180) * distance + 60,
            scale: [0, 1.3, 1],
            opacity: [1, 1, 0],
        },
        transition: {
            duration: 0.9,
            delay,
            ease: [0.25, 0.46, 0.45, 0.94],
        }
    });

    const expression = getFaceExpression();

    return (
        <div
            className={`
                relative flex items-center justify-center overflow-visible
                w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64
                ${showScreenShake && phase === 'popping' ? (showEpicShake ? 'animate-shake-epic' : 'animate-shake') : ''}
            `}
            style={{
                '--shake-intensity': showEpicShake ? '10px' : '5px',
            }}
        >
            {/* === CHOCOLATE ORB === */}
            <AnimatePresence>
                {phase !== 'popping' && phase !== 'complete' && (
                    <motion.div
                        className="absolute w-36 h-36 rounded-full"
                        style={{
                            background: 'radial-gradient(circle at 30% 30%, #D2691E 0%, #8B4513 50%, #5D3A1A 100%)',
                            boxShadow: '0 10px 40px rgba(139, 69, 19, 0.6), inset 0 -10px 20px rgba(93, 58, 26, 0.6)',
                        }}
                        variants={orbVariants}
                        animate={phase}
                    >
                        {/* Shine */}
                        <div
                            className="absolute w-10 h-10 rounded-full"
                            style={{
                                background: 'radial-gradient(circle, rgba(255,255,255,0.7) 0%, transparent 70%)',
                                top: '12%',
                                left: '18%',
                            }}
                        />

                        {/* === EXPRESSIVE FACE === */}
                        {showFace && (phase === 'materializing' || phase === 'rumbling' || phase === 'cracking') && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                {/* Googly Eyes */}
                                <div className="flex gap-5 mb-2">
                                    {[0, 1].map((i) => (
                                        <motion.div
                                            key={i}
                                            className="w-7 h-7 bg-white rounded-full flex items-center justify-center relative overflow-hidden"
                                            style={{ boxShadow: 'inset 0 3px 5px rgba(0,0,0,0.25)' }}
                                            animate={expression === 'scared' ? {
                                                scaleY: 1.2,
                                                scaleX: 1.1,
                                            } : {}}
                                        >
                                            {/* Pupil */}
                                            <motion.div
                                                className="w-3.5 h-3.5 bg-black rounded-full relative"
                                                animate={{
                                                    x: expression === 'worried' ? [0, 3, -3, 3, 0] :
                                                        expression === 'scared' ? [0, 2, -2, 2, -2, 0] : 0,
                                                    y: expression === 'curious' ? -1 :
                                                        expression === 'worried' ? [0, 2, 0] :
                                                            expression === 'scared' ? 2 : 0,
                                                    scale: expression === 'scared' ? 0.8 : 1,
                                                }}
                                                transition={{
                                                    duration: expression === 'worried' ? 0.15 : 0.3,
                                                    repeat: expression === 'worried' || expression === 'scared' ? Infinity : 0
                                                }}
                                            >
                                                {/* Eye highlight */}
                                                <div className="absolute w-1.5 h-1.5 bg-white rounded-full top-0.5 right-0.5" />
                                            </motion.div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Mouth - changes with expression */}
                                <motion.div
                                    className="relative"
                                    animate={{
                                        scale: expression === 'scared' ? 1.3 : 1,
                                    }}
                                >
                                    {expression === 'curious' && (
                                        <div className="w-4 h-4 bg-[#5D3A1A] rounded-full" />
                                    )}
                                    {expression === 'worried' && (
                                        <motion.div
                                            className="w-5 h-5 bg-[#5D3A1A] rounded-full"
                                            animate={{ scaleY: [1, 1.4, 0.9, 1.2, 1], scaleX: [1, 0.85, 1.1, 0.9, 1] }}
                                            transition={{ duration: 0.2, repeat: Infinity }}
                                        />
                                    )}
                                    {expression === 'scared' && (
                                        <motion.div
                                            className="w-6 h-6 bg-[#5D3A1A] rounded-full"
                                            animate={{ scale: [1, 1.2, 1] }}
                                            transition={{ duration: 0.15, repeat: Infinity }}
                                        />
                                    )}
                                </motion.div>

                                {/* Sweat Drop (worried/scared) */}
                                <AnimatePresence>
                                    {(expression === 'worried' || expression === 'scared') && (
                                        <motion.div
                                            className="absolute w-2 h-3"
                                            style={{
                                                right: '20%',
                                                top: '25%',
                                                background: 'linear-gradient(180deg, rgba(135,206,250,0.8) 0%, rgba(65,105,225,0.6) 100%)',
                                                borderRadius: '50% 50% 50% 50% / 30% 30% 70% 70%',
                                            }}
                                            initial={{ y: 0, opacity: 0, scale: 0 }}
                                            animate={{ y: [0, 8], opacity: [0, 1, 0], scale: [0, 1, 0.5] }}
                                            transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 0.3 }}
                                        />
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* === CRACKS === */}
                        {(phase === 'cracking' || phase === 'popping') && (
                            <div className="absolute inset-0">
                                {cracks.map((crack) => (
                                    <React.Fragment key={crack.id}>
                                        {/* Main crack */}
                                        <motion.div
                                            className="absolute left-1/2 top-1/2"
                                            style={{
                                                width: 4,
                                                height: crack.length,
                                                background: 'linear-gradient(to bottom, #FF6B00, #FFD700)',
                                                transformOrigin: 'top center',
                                                rotate: crack.angle,
                                                boxShadow: '0 0 10px #FF6B00, 0 0 20px #FFD700',
                                            }}
                                            initial={{ scaleY: 0, opacity: 0 }}
                                            animate={{ scaleY: 1, opacity: 1 }}
                                            transition={{ delay: crack.delay, duration: 0.12 }}
                                        />
                                        {/* Branch crack */}
                                        {crack.hasBranch && (
                                            <motion.div
                                                className="absolute left-1/2 top-1/2"
                                                style={{
                                                    width: 2,
                                                    height: crack.length * 0.6,
                                                    background: 'linear-gradient(to bottom, #FF8C00, #FFD700)',
                                                    transformOrigin: 'top center',
                                                    rotate: crack.angle + 30,
                                                    boxShadow: '0 0 6px #FF8C00',
                                                }}
                                                initial={{ scaleY: 0, opacity: 0 }}
                                                animate={{ scaleY: 1, opacity: 0.8 }}
                                                transition={{ delay: crack.delay + 0.08, duration: 0.1 }}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        {/* === STEAM === */}
                        {showSteam && (phase === 'rumbling' || phase === 'cracking') && (
                            <div className="absolute -top-6 left-1/2 -translate-x-1/2">
                                {[0, 1, 2, 3].map((i) => (
                                    <motion.div
                                        key={i}
                                        className="absolute w-4 h-8 rounded-full"
                                        style={{
                                            background: 'rgba(255,255,255,0.5)',
                                            left: (i - 1.5) * 14,
                                        }}
                                        animate={{
                                            y: [-15, -50],
                                            opacity: [0.7, 0],
                                            scale: [1, 1.8],
                                        }}
                                        transition={{
                                            duration: 0.9,
                                            repeat: Infinity,
                                            delay: i * 0.15,
                                        }}
                                    />
                                ))}
                            </div>
                        )}

                        {/* === MELTING DRIPS === */}
                        {showMeltingDrips && (phase === 'rumbling' || phase === 'cracking') && (
                            <div className="absolute bottom-0 left-1/2">
                                {meltingDrips.map((drip) => (
                                    <motion.div
                                        key={drip.id}
                                        className="absolute w-3 rounded-b-full"
                                        style={{
                                            left: drip.startX,
                                            top: '100%',
                                            background: 'linear-gradient(180deg, #8B4513 0%, #5D3A1A 100%)',
                                            boxShadow: '0 2px 6px rgba(139, 69, 19, 0.5)',
                                        }}
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{
                                            height: [0, drip.length, drip.length + 10],
                                            opacity: [0, 1, 0.8],
                                        }}
                                        transition={{
                                            delay: drip.delay,
                                            duration: 1.2,
                                            ease: 'easeIn',
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* === UH OH TEXT === */}
            <AnimatePresence>
                {showUhOhText && phase === 'cracking' && (
                    <motion.div
                        className="absolute text-2xl font-bold z-20"
                        style={{
                            color: '#FF4500',
                            textShadow: '2px 2px 0 #FFD700, -2px -2px 0 #FFD700',
                            fontFamily: 'Impact, sans-serif',
                            top: '-30px',
                        }}
                        initial={{ scale: 0, y: 20 }}
                        animate={{ scale: [0, 1.3, 1], y: [20, 0] }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                    >
                        UH OH...
                    </motion.div>
                )}
            </AnimatePresence>

            {/* === EXPLOSION EFFECTS === */}
            <AnimatePresence>
                {phase === 'popping' && (
                    <>
                        {/* Flash */}
                        <motion.div
                            className="absolute w-56 h-56 rounded-full"
                            style={{ background: 'radial-gradient(circle, rgba(255,220,150,0.9) 0%, transparent 70%)' }}
                            initial={{ scale: 0, opacity: 1 }}
                            animate={{ scale: 3.5, opacity: 0 }}
                            transition={{ duration: 0.45 }}
                        />

                        {/* Smoke Rings */}
                        {showSmokeRings && Array.from({ length: smokeRingCount }).map((_, i) => (
                            <motion.div
                                key={`smoke-${i}`}
                                className="absolute rounded-full border-4"
                                style={{
                                    width: 80 + i * 30,
                                    height: 80 + i * 30,
                                    borderColor: 'rgba(200, 180, 160, 0.5)',
                                }}
                                initial={{ scale: 0.3, opacity: 0.8 }}
                                animate={{ scale: 2, opacity: 0, y: -20 * (i + 1) }}
                                transition={{ duration: 0.8, delay: i * 0.15, ease: 'easeOut' }}
                            />
                        ))}

                        {/* Chocolate Particles */}
                        {particles.map((p) => {
                            const anim = createExplosionAnimation(p.angle, p.distance, p.delay);
                            return (
                                <motion.div
                                    key={`particle-${p.id}`}
                                    className="absolute rounded-full"
                                    style={{
                                        width: p.size,
                                        height: p.size,
                                        background: p.color,
                                        boxShadow: `0 3px 10px ${p.color}80`,
                                    }}
                                    {...anim}
                                />
                            );
                        })}

                        {/* Chocolate Chunks with spring physics */}
                        {chunks.map((c) => (
                            <motion.div
                                key={`chunk-${c.id}`}
                                className="absolute rounded-lg"
                                style={{
                                    width: c.size,
                                    height: c.size * 0.7,
                                    background: 'linear-gradient(135deg, #D2691E 0%, #8B4513 100%)',
                                    boxShadow: '0 5px 15px rgba(139, 69, 19, 0.6)',
                                }}
                                initial={{ x: 0, y: 0, rotate: 0, scale: 0 }}
                                animate={{
                                    x: Math.cos((c.angle * Math.PI) / 180) * c.distance,
                                    y: Math.sin((c.angle * Math.PI) / 180) * c.distance + 70,
                                    rotate: c.rotation,
                                    scale: [0, 1.3, 1, 0.7],
                                    opacity: [1, 1, 1, 0],
                                }}
                                transition={{
                                    duration: 1.1,
                                    delay: c.delay,
                                    type: 'spring',
                                    stiffness: 90,
                                    damping: 12,
                                }}
                            />
                        ))}

                        {/* Confetti */}
                        {confetti.map((c) => (
                            <motion.div
                                key={`confetti-${c.id}`}
                                className="absolute"
                                style={{
                                    width: c.size,
                                    height: c.size * 0.6,
                                    background: c.color,
                                    borderRadius: 2,
                                }}
                                initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
                                animate={{
                                    x: [0, c.x, c.x + c.drift],
                                    y: [0, -c.fall * 0.5, c.fall],
                                    rotate: c.spin,
                                    opacity: [1, 1, 0],
                                }}
                                transition={{
                                    duration: 1.3,
                                    delay: c.delay,
                                    ease: 'easeOut',
                                }}
                            />
                        ))}

                        {/* Golden Sparkles */}
                        {showGoldenSparkles && Array.from({ length: 8 + (intensity - 2) * 5 }).map((_, i) => (
                            <motion.div
                                key={`sparkle-${i}`}
                                className="absolute"
                                style={{
                                    width: 10,
                                    height: 10,
                                    background: '#FFD700',
                                    boxShadow: '0 0 10px #FFD700, 0 0 20px #FFA500',
                                    clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                                }}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{
                                    x: -80 + Math.random() * 160,
                                    y: -80 + Math.random() * 160,
                                    scale: [0, 1.8, 1, 0],
                                    opacity: [0, 1, 1, 0],
                                    rotate: 360,
                                }}
                                transition={{
                                    duration: 0.9,
                                    delay: Math.random() * 0.5,
                                }}
                            />
                        ))}

                        {/* Screen Edge Splatter */}
                        {showSplatter && (
                            <>
                                {Array.from({ length: 4 + intensity }).map((_, i) => (
                                    <motion.div
                                        key={`splat-${i}`}
                                        className="absolute rounded-full"
                                        style={{
                                            width: 15 + Math.random() * 20,
                                            height: 15 + Math.random() * 20,
                                            background: chocolateColors[i % chocolateColors.length],
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                        }}
                                        initial={{
                                            x: 0,
                                            y: 0,
                                            scale: 0,
                                        }}
                                        animate={{
                                            x: (Math.random() > 0.5 ? 1 : -1) * (100 + Math.random() * 50),
                                            y: (Math.random() > 0.5 ? 1 : -1) * (80 + Math.random() * 50),
                                            scale: [0, 1.5, 1.2],
                                            opacity: [0, 1, 0.8],
                                        }}
                                        transition={{
                                            duration: 0.5,
                                            delay: i * 0.05,
                                            ease: 'easeOut',
                                        }}
                                    />
                                ))}
                            </>
                        )}

                        {/* Comic Text POP! */}
                        {showComicText && (
                            <motion.div
                                className="absolute text-5xl font-bold z-30"
                                style={{
                                    color: '#FFD700',
                                    textShadow: '3px 3px 0 #8B4513, -3px -3px 0 #8B4513, 3px -3px 0 #8B4513, -3px 3px 0 #8B4513, 0 0 20px rgba(255,215,0,0.5)',
                                    fontFamily: 'Impact, sans-serif',
                                }}
                                initial={{ scale: 0, rotate: -15 }}
                                animate={{ scale: [0, 1.5, 1.2], rotate: [0, 8, 0] }}
                                transition={{ duration: 0.35, delay: 0.08 }}
                            >
                                POP!
                            </motion.div>
                        )}

                        {/* Chocolate Rain (high tiers) */}
                        {showChocolateRain && Array.from({ length: (intensity - 4) * 8 }).map((_, i) => (
                            <motion.div
                                key={`rain-${i}`}
                                className="absolute w-2 h-5 rounded-full"
                                style={{
                                    background: chocolateColors[i % chocolateColors.length],
                                    left: -140 + Math.random() * 280,
                                    top: -80,
                                }}
                                initial={{ y: 0, opacity: 0 }}
                                animate={{ y: 240, opacity: [0, 1, 1, 0] }}
                                transition={{
                                    duration: 1.1,
                                    delay: 0.25 + Math.random() * 0.6,
                                    ease: 'easeIn',
                                }}
                            />
                        ))}
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CocoaPopAnimation;
